import { useEffect, useMemo, useState } from "react";
import { Newspaper, X, Search, CalendarDays, User, Users, Clock, SlidersHorizontal } from "lucide-react";
import { endpoints } from "@/lib/api";
import { timeAgo } from "@/lib/fbCache";
import { NewsBody, AuthorCard } from "@/components/NewsBody";
import { AdSlot } from "@/components/AdSlot";

const fmtFull = (iso) => (iso ? new Date(iso).toLocaleString([], { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const sel = "h-10 rounded-xl bg-[#0A0C10] border border-[#273042] px-3 text-sm text-white outline-none focus:border-[#E11D2A]/60";

const Meta = ({ n, nowMs, id }) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400" data-testid={`news-meta-${id}`}>
    <span className="inline-flex items-center gap-1" data-testid={`news-author-${id}`}><User className="w-3 h-3" />{n.authorName}</span>
    {n.team && <span className="inline-flex items-center gap-1" data-testid={`news-team-${id}`}><Users className="w-3 h-3" />{n.team}</span>}
    <span className="inline-flex items-center gap-1" data-testid={`news-date-${id}`}><CalendarDays className="w-3 h-3" />{fmtFull(n.publishedAt)}</span>
    <span className="inline-flex items-center gap-1 text-[#EF4444] font-semibold" data-testid={`news-age-${id}`}><Clock className="w-3 h-3" />{timeAgo(n.publishedAt, nowMs)}</span>
  </div>
);

function Filters({ f, setF, authors, teams, onReset, active }) {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="rounded-2xl bg-[#121620] border border-[#273042] p-3 sm:p-4 mb-5 grid grid-cols-1 gap-3 min-w-0 [&>*]:min-w-0" data-testid="news-filters">
      <label className="flex items-center gap-2 h-11 rounded-xl bg-[#0A0C10] border border-[#273042] px-3 focus-within:border-[#E11D2A]/60">
        <Search className="w-4 h-4 text-slate-500" />
        <input value={f.q} onChange={set("q")} placeholder="Search news by keyword…" data-testid="news-search-input" className="flex-1 min-w-0 bg-transparent text-sm text-white outline-none placeholder-slate-500" />
        {f.q && <button onClick={() => setF({ ...f, q: "" })} data-testid="news-search-clear"><X className="w-4 h-4 text-slate-500" /></button>}
      </label>
      <div className="grid grid-cols-1 min-[360px]:grid-cols-2 lg:grid-cols-5 gap-2 items-end [&>*]:min-w-0">
        <select value={f.author} onChange={set("author")} className={`${sel} w-full`} data-testid="news-filter-author" aria-label="Author">
          <option value="">All authors</option>{authors.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={f.team} onChange={set("team")} className={`${sel} w-full`} data-testid="news-filter-team" aria-label="Newsroom team">
          <option value="">All teams / desks</option>{teams.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold">From<input type="date" value={f.from} onChange={set("from")} className={`${sel} w-full min-w-0 mt-1`} data-testid="news-filter-from" /></label>
        <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold">To<input type="date" value={f.to} onChange={set("to")} className={`${sel} w-full min-w-0 mt-1`} data-testid="news-filter-to" /></label>
        <button onClick={onReset} disabled={!active} data-testid="news-filter-reset" className="h-10 rounded-xl border border-[#273042] text-sm text-slate-300 hover:text-white disabled:opacity-40 min-[360px]:col-span-2 lg:col-span-1 inline-flex items-center justify-center gap-1.5"><SlidersHorizontal className="w-4 h-4" /> Clear filters</button>
      </div>
    </div>
  );
}

const EMPTY = { q: "", author: "", team: "", from: "", to: "" };

export default function NewsPage() {
  const [news, setNews] = useState(null);
  const [open, setOpen] = useState(null);
  const [cfg, setCfg] = useState({});
  const [newsCats, setNewsCats] = useState([]);
  const [cat, setCat] = useState("All");
  const [lang, setLang] = useState("All");
  const [f, setF] = useState(EMPTY);
  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => {
    endpoints.news().then((r) => setNews(r.data || [])).catch(() => setNews([]));
    endpoints.settings().then((r) => setCfg(r.data || {})).catch(() => {});
    endpoints.newsCategories().then((r) => setNewsCats((r.data || []).map((c) => c.name))).catch(() => {});
    const t = setInterval(() => setNowMs(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  const list = useMemo(() => news || [], [news]);
  const cats = ["All", ...(newsCats.length ? newsCats : (cfg.newsCategories || ["Sports", "Politics", "Entertainment"]))];
  const langs = ["All", ...(cfg.newsLanguages || ["English", "Kinyarwanda"])];
  const authors = useMemo(() => [...new Set(list.map((n) => n.authorName).filter(Boolean))].sort(), [list]);
  const teams = useMemo(() => [...new Set(list.map((n) => n.team).filter(Boolean))].sort(), [list]);
  const q = f.q.trim().toLowerCase();
  const filtered = list.filter((n) => {
    const day = (n.publishedAt || "").slice(0, 10);
    return (cat === "All" || (n.category || "").toLowerCase() === cat.toLowerCase()) &&
      (lang === "All" || (n.language || "").toLowerCase() === lang.toLowerCase()) &&
      (!q || [n.title, n.summary, n.body, n.category, n.authorName, n.team].join(" ").toLowerCase().includes(q)) &&
      (!f.author || n.authorName === f.author) && (!f.team || n.team === f.team) &&
      (!f.from || day >= f.from) && (!f.to || day <= f.to);
  });
  const active = JSON.stringify(f) !== JSON.stringify(EMPTY) || cat !== "All" || lang !== "All";
  const reset = () => { setF(EMPTY); setCat("All"); setLang("All"); };

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-10" data-testid="news-page">
      <div className="flex items-center gap-2 mb-2">
        <Newspaper className="w-7 h-7 text-[#E11D2A]" />
        <h1 className="text-3xl sm:text-4xl font-extrabold text-white">News</h1>
      </div>
      <p className="text-slate-400 mb-6">Latest updates from B&B KIGALI.</p>
      <AdSlot placement="news" position="top" className="mb-6" />
      <Filters f={f} setF={setF} authors={authors} teams={teams} onReset={reset} active={active} />
      <div className="flex flex-wrap gap-2 mb-3" data-testid="news-categories">
        {cats.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`news-cat-${c}`}
            className={`h-9 px-4 rounded-full text-sm font-semibold border transition-colors ${cat === c ? "bg-[#E11D2A] border-[#E11D2A] text-white" : "border-[#273042] bg-[#121620] text-slate-300 hover:text-white"}`}>{c}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {langs.map((l) => (
          <button key={l} onClick={() => setLang(l)} data-testid={`news-lang-${l}`}
            className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${lang === l ? "bg-[#2563EB] border-[#2563EB] text-white" : "border-[#273042] bg-[#121620] text-slate-400 hover:text-white"}`}>{l}</button>
        ))}
        <span className="ml-auto text-xs text-slate-500" data-testid="news-count">{news ? `${filtered.length} of ${list.length} article${list.length === 1 ? "" : "s"}` : "Loading…"}</span>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {news && filtered.length === 0 ? <div className="text-slate-500 col-span-full py-10 text-center" data-testid="news-empty">{active ? "No articles match your search or filters." : "No articles yet."}</div> : filtered.map((n) => (
          <button key={n.id} onClick={() => setOpen(n)} data-testid={`news-card-${n.id}`}
            className="text-left rounded-xl bg-[#121620] border border-[#273042] overflow-hidden card-hover flex flex-col">
            <div className="aspect-video overflow-hidden">
              <img src={n.coverUrl || n.thumbnail || "/bb-logo.png"} alt={n.title} loading="lazy" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = "/bb-logo.png"; }} />
            </div>
            <div className="p-5 flex flex-col flex-1">
              <div className="text-[10px] uppercase tracking-wider text-[#E11D2A] font-bold">{n.category || "News"}</div>
              <h3 className="text-white font-semibold mt-1.5 line-clamp-2">{n.title}</h3>
              <p className="text-sm text-slate-400 mt-2 line-clamp-3 flex-1">{n.summary}</p>
              <div className="mt-4 pt-3 border-t border-[#273042]"><Meta n={n} nowMs={nowMs} id={n.id} /></div>
            </div>
          </button>
        ))}
      </div>

      <AdSlot placement="news" position="bottom" className="mt-8" />

      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="news-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setOpen(null)} />
          <div className="relative w-full max-w-2xl glass border border-[#273042] rounded-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
            <button onClick={() => setOpen(null)} data-testid="news-modal-close" className="absolute right-4 top-4 z-10 w-9 h-9 rounded-full bg-black/50 text-white flex items-center justify-center"><X className="w-5 h-5" /></button>
            <img src={open.coverUrl || open.thumbnail || "/bb-logo.png"} alt={open.title} className="w-full aspect-video object-cover" onError={(e) => { e.currentTarget.src = "/bb-logo.png"; }} />
            <div className="p-7">
              <div className="text-[10px] uppercase tracking-wider text-[#E11D2A] font-bold">{open.category || "News"}</div>
              <h2 className="text-2xl font-extrabold text-white mt-2">{open.title}</h2>
              <div className="mt-3"><Meta n={open} nowMs={nowMs} id="modal" /></div>
              {open.body ? <NewsBody text={open.body} /> : <p className="text-slate-300 mt-4 leading-relaxed whitespace-pre-line">{open.summary}</p>}
              <AuthorCard n={open} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
